import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Trash2, Image, ExternalLink, Facebook } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGalleryPage = () => {
  // ===== IMAGES STATE =====
  const [images, setImages] = useState([]);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageCategory, setNewImageCategory] = useState('event');

  // ===== FACEBOOK ALBUMS STATE =====
  const [albums, setAlbums] = useState([]);
  const [isAlbumDialogOpen, setIsAlbumDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumFormData, setAlbumFormData] = useState({
    id: '',
    title: '',
    album_url: '',
    category: 'event',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchImages();
    fetchAlbums();
  }, []);

  // ===== IMAGES FUNCTIONS =====
  const fetchImages = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl) {
      toast.error('Veuillez entrer une URL');
      return;
    }
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/gallery`, {
        id: `img-${Date.now()}`,
        url: newImageUrl,
        title: newImageTitle,
        category: newImageCategory
      }, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Image ajoutée!');
      setIsImageDialogOpen(false);
      setNewImageUrl('');
      setNewImageTitle('');
      fetchImages();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Image supprimée!');
      fetchImages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ===== FACEBOOK ALBUMS FUNCTIONS =====
  const fetchAlbums = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/facebook-albums`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setAlbums(response.data.albums || []);
    } catch (error) {
      console.error('Error fetching albums:', error);
    }
  };

  const handleSubmitAlbum = async () => {
    if (!albumFormData.title || !albumFormData.album_url) {
      toast.error('Titre et URL sont requis');
      return;
    }
    const auth = localStorage.getItem('admin_auth');
    try {
      if (editingAlbum) {
        await axios.put(`${BACKEND_URL}/api/admin/facebook-albums/${editingAlbum.id}`, albumFormData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Album mis à jour!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/facebook-albums`, albumFormData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Album ajouté!');
      }
      setIsAlbumDialogOpen(false);
      resetAlbumForm();
      fetchAlbums();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet album?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/facebook-albums/${albumId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Album supprimé!');
      fetchAlbums();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditAlbumDialog = (album) => {
    setEditingAlbum(album);
    setAlbumFormData(album);
    setIsAlbumDialogOpen(true);
  };

  const resetAlbumForm = () => {
    setEditingAlbum(null);
    setAlbumFormData({
      id: '',
      title: '',
      album_url: '',
      category: 'event',
      order: 0,
      active: true
    });
  };

  return (
    <div data-testid="admin-gallery-page">
      <div className="mb-8">
        <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Galerie</h1>
        <p className="text-sm text-[#B9B2A8]">Gérez les images et albums Facebook</p>
      </div>

      <Tabs defaultValue="images" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="facebook">Albums Facebook</TabsTrigger>
        </TabsList>

        {/* ===== IMAGES TAB ===== */}
        <TabsContent value="images">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-[#B9B2A8]">Images individuelles pour le site</p>
            <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une image
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0B0B0C] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-[#F5F0E8] font-display">Ajouter une image</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">URL de l'image</label>
                    <Input
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="https://..."
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                    <p className="text-xs text-[#B9B2A8] mt-1">
                      Uploadez votre image sur Imgur/Cloudinary, puis collez l'URL ici.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre (optionnel)</label>
                    <Input
                      value={newImageTitle}
                      onChange={(e) => setNewImageTitle(e.target.value)}
                      placeholder="Open Air 2024"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Catégorie</label>
                    <Select value={newImageCategory} onValueChange={setNewImageCategory}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B0B0C] border-white/10">
                        <SelectItem value="event" className="text-[#F5F0E8]">Événement</SelectItem>
                        <SelectItem value="experience" className="text-[#F5F0E8]">Expérience</SelectItem>
                        <SelectItem value="crowd" className="text-[#F5F0E8]">Foule</SelectItem>
                        <SelectItem value="dj" className="text-[#F5F0E8]">DJ</SelectItem>
                        <SelectItem value="venue" className="text-[#F5F0E8]">Lieu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newImageUrl && (
                    <div className="mt-4">
                      <p className="text-xs text-[#B9B2A8] mb-2">Prévisualisation:</p>
                      <img src={newImageUrl} alt="Preview" className="w-full rounded-lg" />
                    </div>
                  )}
                  <Button onClick={handleAddImage} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                    Ajouter
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="relative group aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10"
              >
                <img src={image.url} alt={image.title || ''} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a href={image.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="icon" className="text-white hover:text-[#C9A84C]">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteImage(image.id)} className="text-white hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {image.title && (
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs text-white truncate">{image.title}</p>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className="text-[10px] bg-black/60 text-white px-2 py-0.5 rounded">
                    {image.category}
                  </span>
                </div>
              </motion.div>
            ))}
            {images.length === 0 && (
              <div className="col-span-full text-center py-12 text-[#B9B2A8]">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune image. Cliquez sur "Ajouter" pour commencer.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== FACEBOOK ALBUMS TAB ===== */}
        <TabsContent value="facebook">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-[#B9B2A8]">Albums Facebook pour affichage public</p>
            <Dialog open={isAlbumDialogOpen} onOpenChange={(open) => { setIsAlbumDialogOpen(open); if (!open) resetAlbumForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un album
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0B0B0C] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-[#F5F0E8] font-display">
                    {editingAlbum ? 'Modifier l\'album' : 'Nouvel album Facebook'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre de l'album</label>
                    <Input
                      value={albumFormData.title}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, title: e.target.value })}
                      placeholder="Open Air 2024 - Photos"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">URL de l'album Facebook</label>
                    <Input
                      value={albumFormData.album_url}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, album_url: e.target.value })}
                      placeholder="https://www.facebook.com/media/set/?set=..."
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                    <p className="text-xs text-[#B9B2A8] mt-1">
                      Collez l'URL complète de votre album Facebook public
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Catégorie</label>
                    <Select value={albumFormData.category} onValueChange={(v) => setAlbumFormData({ ...albumFormData, category: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B0B0C] border-white/10">
                        <SelectItem value="event" className="text-[#F5F0E8]">Événement</SelectItem>
                        <SelectItem value="experience" className="text-[#F5F0E8]">Expérience</SelectItem>
                        <SelectItem value="crowd" className="text-[#F5F0E8]">Foule</SelectItem>
                        <SelectItem value="venue" className="text-[#F5F0E8]">Lieu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Ordre d'affichage</label>
                      <Input
                        type="number"
                        value={albumFormData.order}
                        onChange={(e) => setAlbumFormData({ ...albumFormData, order: parseInt(e.target.value) || 0 })}
                        className="bg-white/5 border-white/10 text-[#F5F0E8]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Statut</label>
                      <Select value={albumFormData.active ? 'active' : 'inactive'} onValueChange={(v) => setAlbumFormData({ ...albumFormData, active: v === 'active' })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0B0C] border-white/10">
                          <SelectItem value="active" className="text-[#F5F0E8]">Actif</SelectItem>
                          <SelectItem value="inactive" className="text-[#F5F0E8]">Inactif</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleSubmitAlbum} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                    {editingAlbum ? 'Mettre à jour' : 'Ajouter'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {albums.map((album, index) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-xl p-4 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Facebook className="w-5 h-5 text-[#C9A84C]" />
                    <h3 className="text-sm text-[#F5F0E8] font-medium">{album.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditAlbumDialog(album)} className="h-7 w-7 text-[#B9B2A8] hover:text-[#F5F0E8]">
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteAlbum(album.id)} className="h-7 w-7 text-[#B9B2A8] hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 text-xs text-[#B9B2A8]">
                  <p><span className="text-[#F5F0E8]">Catégorie:</span> {album.category}</p>
                  <p><span className="text-[#F5F0E8]">Ordre:</span> {album.order}</p>
                  <p>
                    <span className="text-[#F5F0E8]">Statut:</span>{' '}
                    <span className={album.active ? 'text-green-400' : 'text-red-400'}>
                      {album.active ? 'Actif' : 'Inactif'}
                    </span>
                  </p>
                  <a href={album.album_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline block truncate">
                    {album.album_url}
                  </a>
                </div>
              </motion.div>
            ))}
            {albums.length === 0 && (
              <div className="col-span-full text-center py-12 text-[#B9B2A8]">
                <Facebook className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun album Facebook. Cliquez sur "Ajouter" pour commencer.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminGalleryPage;
